import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  DialogActionTrigger,
  HStack,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Heading,
  Link,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { JsonReadonlyBlock } from '#src/components/json-readonly-block';
import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import { toaster } from '#src/components/ui/toaster';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { GET_AI_AUDIT_BY_ID } from '#src/graphql/queries/ai-audit';

const ASK_AI_AUDIT_PROCESSOR_ID = 'ask-ai-audit-prompt';

function formatAskAiAuditClipboardCopy(
  question: string,
  answer: string,
): string {
  return `The users question was:\n\n${question.trim()}\n\nThe AI's response was:\n\n${answer}`;
}

interface AskAiAuditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditInput: unknown;
  auditOutput: unknown;
}

function AskAiAuditModal({
  open,
  onOpenChange,
  auditInput,
  auditOutput,
}: AskAiAuditModalProps) {
  const [question, setQuestion] = React.useState('');
  const [answer, setAnswer] = React.useState<string | null>(null);
  const [executeProcessor, { loading }] = useMutation(EXECUTE_PROCESSOR);

  const handleClose = React.useCallback(() => {
    setQuestion('');
    setAnswer(null);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = React.useCallback(async () => {
    const q = question.trim();
    if (!q) return;
    try {
      const { data } = await executeProcessor({
        variables: {
          input: {
            processorId: ASK_AI_AUDIT_PROCESSOR_ID,
            variables: {
              question: q,
              auditInput,
              auditOutput,
            },
          },
        },
      });
      const result = data?.executeProcessor;
      if (result?.success && result?.result != null) {
        const message =
          typeof result.result === 'object' && 'message' in result.result
            ? result.result.message
            : undefined;
        if (typeof message === 'string') {
          setAnswer(message);
        } else {
          throw new Error('Processor did not return a message');
        }
      } else {
        throw new Error(result?.error ?? 'Processor failed');
      }
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Failed to get AI response',
        type: 'error',
      });
    }
  }, [auditInput, auditOutput, executeProcessor, question]);

  const handleCopyQuestionAndAnswer = React.useCallback(() => {
    if (answer == null) return;
    const text = formatAskAiAuditClipboardCopy(question, answer);
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        toaster.create({
          title: 'Copied',
          description: 'Question and response copied to clipboard',
          type: 'success',
        });
      })
      .catch(() => {
        toaster.create({
          title: 'Copy failed',
          description: 'Could not copy to clipboard',
          type: 'error',
        });
      });
  }, [answer, question]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="md"
    >
      <CenteredDialogContent maxW="lg">
        <DialogHeader>
          <DialogTitle>Ask AI about this audit</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Ask a question about this logged AI interaction. The model sees
              the same input and output JSON shown on this page (large payloads
              may be truncated server-side).
            </Text>
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g., What model was requested? Summarize the error…"
              rows={4}
              resize="vertical"
              minH="100px"
              disabled={loading}
            />
            {answer != null && (
              <Box>
                <HStack justify="space-between" align="center" mb={2}>
                  <Text fontSize="xs" color="fg.muted">
                    Response
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={handleCopyQuestionAndAnswer}
                    title="Copy question and AI response (formatted for pasting elsewhere)"
                  >
                    Copy Q&A
                  </Button>
                </HStack>
                <Box
                  p={3}
                  borderRadius="md"
                  bg="bg.muted"
                  borderWidth="1px"
                  borderColor="border"
                  maxH="240px"
                  overflowY="auto"
                >
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {answer}
                  </Text>
                </Box>
              </Box>
            )}
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogActionTrigger>
          <Button
            colorScheme="purple"
            onClick={handleSubmit}
            disabled={!question.trim() || loading}
            loading={loading}
            loadingText="Asking…"
          >
            Ask
          </Button>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}

export function AiAuditDetailPage() {
  const { id } = useParams<{ id: string }>();
  const auditId = id ?? '';
  const [askOpen, setAskOpen] = React.useState(false);

  const { data, loading, error, refetch } = useQuery(GET_AI_AUDIT_BY_ID, {
    variables: { id: auditId },
    skip: !auditId,
  });

  if (!auditId) {
    return (
      <Box>
        <Text color="fg.muted">Missing audit id.</Text>
        <Link asChild color="blue.600" mt={2} display="inline-block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Spinner />
        <Text mt={2}>Loading audit…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Text color="red.500">Error: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
        <Link asChild color="blue.600" mt={2} display="block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  const audit = data?.aiAudit;
  if (!audit) {
    return (
      <Box>
        <Heading size="md" mb={2}>
          Not found
        </Heading>
        <Text color="fg.muted">
          No audit row for id{' '}
          <Text as="span" fontFamily="mono">
            {auditId}
          </Text>
          .
        </Text>
        <Link asChild color="blue.600" mt={4} display="inline-block">
          <RouterLink to="/ai-audit">Back to list</RouterLink>
        </Link>
      </Box>
    );
  }

  return (
    <VStack gap={6} align="stretch">
      <AskAiAuditModal
        open={askOpen}
        onOpenChange={setAskOpen}
        auditInput={audit.input}
        auditOutput={audit.output}
      />
      <Box>
        <Link asChild color="blue.600" mb={2} display="inline-block">
          <RouterLink to="/ai-audit">← AI audit log</RouterLink>
        </Link>
        <HStack
          justify="space-between"
          align="flex-start"
          flexWrap="wrap"
          gap={3}
        >
          <Box>
            <Heading size="lg" color="fg">
              Audit detail
            </Heading>
            <Text fontSize="sm" color="fg.muted" fontFamily="mono">
              {audit.id}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {new Date(audit.createdAt).toLocaleString()}
            </Text>
          </Box>
          <Button
            colorScheme="purple"
            size="sm"
            onClick={() => setAskOpen(true)}
            flexShrink={0}
          >
            Ask AI
          </Button>
        </HStack>
        <Text fontSize="sm" color="fg.muted" maxW="48rem" mt={2}>
          Input is the HTTP request body sent to OpenAI. For document AI edit,
          the{' '}
          <Text as="span" fontFamily="mono">
            model
          </Text>{' '}
          field there is the value used after the server validates{' '}
          <Text as="span" fontFamily="mono">
            variables.model
          </Text>
          . If that differs from what you picked in the UI, the choice was not
          on the allowlist or did not reach the processor. In Output,{' '}
          <Text as="span" fontFamily="mono">
            model
          </Text>{' '}
          is OpenAI&apos;s canonical id and may look like a dated snapshot (e.g.{' '}
          <Text as="span" fontFamily="mono">
            gpt-4o-2024-08-06
          </Text>
          ) even when the request used a short alias.
        </Text>
      </Box>
      <JsonReadonlyBlock title="Input" value={audit.input} />
      <JsonReadonlyBlock title="Output" value={audit.output} />
    </VStack>
  );
}
